import { serializer } from "luadata"
import { Branded } from "../../utils/types"
import { split, splitIntoTuples } from "./array"
import { z } from "zod"

export type LRTemplateFileContents = Branded<string, "LRTemplateFileContents">
export type LRTemplateLUATable = Branded<string, "LRTemplateLUATable">

const LRTemplateSchema = z.object({
	id: z.string(),
	internalName: z.string().optional(),
	title: z.string(),
	type: z.literal("Develop"),
	version: z.number(),
	value: z.object({
		uuid: z.string().optional(),
		settings: z.object({
			AutoLateralCA: z.number().optional(),
			Blacks2012: z.number().optional(),
			BlueHue: z.number().optional(),
			BlueSaturation: z.number().optional(),
			CameraProfile: z.string().optional(),
			ChromaticAberrationB: z.number().optional(),
			ChromaticAberrationR: z.number().optional(),
			Clarity2012: z.number().optional(),
			ColorNoiseReduction: z.number().optional(),
			ColorNoiseReductionDetail: z.number().optional(),
			ColorNoiseReductionSmoothness: z.number().optional(),
			Contrast2012: z.number().optional(),
			ConvertToGrayscale: z.boolean().optional(),
			Defringe: z.number().optional(),
			DefringeGreenAmount: z.number().optional(),
			DefringeGreenHueHi: z.number().optional(),
			DefringeGreenHueLo: z.number().optional(),
			DefringePurpleAmount: z.number().optional(),
			DefringePurpleHueHi: z.number().optional(),
			DefringePurpleHueLo: z.number().optional(),
			EnableCalibration: z.boolean().optional(),
			EnableCircularGradientBasedCorrections: z.boolean().optional(),
			EnableColorAdjustments: z.boolean().optional(),
			EnableDetail: z.boolean().optional(),
			EnableGradientBasedCorrections: z.boolean().optional(),
			EnableLensCorrections: z.boolean().optional(),
			EnableSplitToning: z.boolean().optional(),
			GreenHue: z.number().optional(),
			GreenSaturation: z.number().optional(),
			Highlights2012: z.number().optional(),
			HueAdjustmentAqua: z.number().optional(),
			HueAdjustmentBlue: z.number().optional(),
			HueAdjustmentGreen: z.number().optional(),
			HueAdjustmentMagenta: z.number().optional(),
			HueAdjustmentOrange: z.number().optional(),
			HueAdjustmentPurple: z.number().optional(),
			HueAdjustmentRed: z.number().optional(),
			HueAdjustmentYellow: z.number().optional(),
			LuminanceAdjustmentAqua: z.number().optional(),
			LuminanceAdjustmentBlue: z.number().optional(),
			LuminanceAdjustmentGreen: z.number().optional(),
			LuminanceAdjustmentMagenta: z.number().optional(),
			LuminanceAdjustmentOrange: z.number().optional(),
			LuminanceAdjustmentPurple: z.number().optional(),
			LuminanceAdjustmentRed: z.number().optional(),
			LuminanceAdjustmentYellow: z.number().optional(),
			LuminanceNoiseReductionContrast: z.number().optional(),
			LuminanceNoiseReductionDetail: z.number().optional(),
			LuminanceSmoothing: z.number().optional(),
			ParametricDarks: z.number().optional(),
			ParametricHighlightSplit: z.number().optional(),
			ParametricHighlights: z.number().optional(),
			ParametricLights: z.number().optional(),
			ParametricMidtoneSplit: z.number().optional(),
			ParametricShadowSplit: z.number().optional(),
			ParametricShadows: z.number().optional(),
			ProcessVersion: z.string().optional(),
			RedHue: z.number().optional(),
			RedSaturation: z.number().optional(),
			Saturation: z.number().optional(),
			SaturationAdjustmentAqua: z.number().optional(),
			SaturationAdjustmentBlue: z.number().optional(),
			SaturationAdjustmentGreen: z.number().optional(),
			SaturationAdjustmentMagenta: z.number().optional(),
			SaturationAdjustmentOrange: z.number().optional(),
			SaturationAdjustmentPurple: z.number().optional(),
			SaturationAdjustmentRed: z.number().optional(),
			SaturationAdjustmentYellow: z.number().optional(),
			ShadowTint: z.number().optional(),
			Shadows2012: z.number().optional(),
			SharpenDetail: z.number().optional(),
			SharpenEdgeMasking: z.number().optional(),
			SharpenRadius: z.number().optional(),
			Sharpness: z.number().optional(),
			SplitToningBalance: z.number().optional(),
			SplitToningHighlightHue: z.number().optional(),
			SplitToningHighlightSaturation: z.number().optional(),
			SplitToningShadowHue: z.number().optional(),
			SplitToningShadowSaturation: z.number().optional(),
			ToneCurveName2012: z.string().optional(),
			ToneCurvePV2012: z.array(z.number()).optional(),
			ToneCurvePV2012Blue: z.array(z.number()).optional(),
			ToneCurvePV2012Green: z.array(z.number()).optional(),
			ToneCurvePV2012Red: z.array(z.number()).optional(),
			Vibrance: z.number().optional(),
			Whites2012: z.number().optional(),
			orientation: z.string().optional(),
		}),
	}),
})

type LRTemplate = z.TypeOf<typeof LRTemplateSchema>

const prepareLuaTable = (text: LRTemplateFileContents): LRTemplateLUATable => {
	if (!text.startsWith("s = {")) {
		throw new Error("Unrecognized LRTemplate format")
	}

	return text.replace("s = ", "") as LRTemplateLUATable
}

const validateLRTemplate = (lrt: unknown): LRTemplate => {
	return LRTemplateSchema.parse(lrt)
}

const parseLRTemplate = (text: LRTemplateFileContents) => {
	const luaTable = prepareLuaTable(text)
	const lrt = serializer.unserialize(luaTable, { dictType: "object" })

	return validateLRTemplate(lrt)
}

const formatXMLValue = (value: unknown): string => {
	if (typeof value === "boolean") {
		return value ? "True" : "False"
	}

	if (!isNaN(value as number)) {
		if (value === 0) {
			return "0"
		}

		return (value as number) > 0 ? `+${value}` : `${value}`
	}

	return value as string
}

const formatRoot = (doc: XMLDocument) => {
	const root = doc.createElement("x:xmpmeta")
	root.setAttribute("xmlns:x", "adobe:ns:meta/")
	root.setAttribute("x:xmptk", "Adobe XMP Core 7.0-c000 1.000000, 0000/00/00-00:00:00")

	return root
}

const formatRDF = (doc: XMLDocument) => {
	const rdfRDF = doc.createElement("rdf:RDF")
	rdfRDF.setAttribute("xmlns:rdf", "http://www.w3.org/1999/02/22-rdf-syntax-ns#")

	return rdfRDF
}

const formatRDFDescription = (doc: XMLDocument, lrTemplate: LRTemplate) => {
	const formatMeta = createMetaFormatter(doc)
	const formatToneCurve = createToneCurveFormatter(doc)

	const [settingEntries, toneCurveEntries] = split(
		Object.entries(lrTemplate.value.settings),
		(entry) => !entry[0].startsWith("ToneCurvePV"),
	)

	const desc = doc.createElement("rdf:Description")

	desc.setAttribute("xmlns:crs", "http://ns.adobe.com/camera-raw-settings/1.0/")
	desc.setAttribute("crs:PresetType", "Normal")
	lrTemplate.value.uuid && desc.setAttribute("crs:UUID", lrTemplate.value.uuid)

	settingEntries.forEach(([key, value]) => {
		if (key === "ProcessVersion") {
			desc.setAttribute(`crs:${key}`, value.toString())
			return
		}

		desc.setAttribute(`crs:${key}`, formatXMLValue(value))
	})

	toneCurveEntries.forEach(([key, value]) => {
		const tuples = splitIntoTuples<number>(value as number[])
		desc.appendChild(formatToneCurve(`crs:${key}`, tuples))
	})

	desc.appendChild(formatMeta("crs:Name", lrTemplate.title))
	desc.appendChild(formatMeta("crs:ShortName", ""))
	desc.appendChild(formatMeta("crs:SortName", ""))
	desc.appendChild(formatMeta("crs:Group", lrTemplate.title))
	desc.appendChild(formatMeta("crs:Description", ""))

	return desc
}

const createMetaFormatter = (doc: XMLDocument) => (tag: string, textContent: string) => {
	const outer = doc.createElement(tag)
	const rdfAlt = doc.createElement("rdf:Alt")
	const rdfLi = doc.createElement("rdf:li")

	rdfLi.setAttribute("xml:lang", "x-default")
	rdfLi.textContent = textContent
	rdfAlt.appendChild(rdfLi)
	outer.appendChild(rdfAlt)

	return outer
}

const createToneCurveFormatter =
	(doc: XMLDocument) => (tag: string, values: [number, number][]) => {
		const outer = doc.createElement(tag)
		const rdfSeq = doc.createElement("rdf:Seq")

		for (const [x, y] of values) {
			const rdfLi = doc.createElement("rdf:li")
			rdfLi.textContent = `${x}, ${y}`
			rdfSeq.appendChild(rdfLi)
		}

		outer.appendChild(rdfSeq)
		return outer
	}

const formatXMP = (lrTemplate: LRTemplate) => {
	const doc = document.implementation.createDocument("", "", null)

	const root = formatRoot(doc)
	const rdf = formatRDF(doc)
	const desc = formatRDFDescription(doc, lrTemplate)

	rdf.appendChild(desc)
	root.appendChild(rdf)

	return new XMLSerializer().serializeToString(root)
}

/**
 * Converts an LRTemplate to XMP
 *
 * @param template
 * @returns the LRTemplate as an XMP string
 */
export const convertToXMP = (template: LRTemplateFileContents): string => {
	const parsed = parseLRTemplate(template)
	return formatXMP(parsed)
}
